import { css } from 'aphrodite';
import * as React from 'react';
import { styleSheet } from './styles';

interface ExpandableTextProps {
	/** The text content to display */
	text: string;
	/** Optional prefix shown before the text in bold (e.g., "Donation Details: ") */
	prefix?: string;
	/** Maximum number of lines to show before truncation (default: 2) */
	maxLines?: number;
}

/**
 * Displays text with truncation and expand/collapse functionality.
 * When text exceeds the specified number of lines, it shows a "Show more" link
 * that expands to reveal the full content.
 */
export const ExpandableText = ({ text, prefix, maxLines = 2 }: ExpandableTextProps) => {
	const [isExpanded, setIsExpanded] = React.useState(false);
	const [needsTruncation, setNeedsTruncation] = React.useState(false);
	const textRef = React.useRef<HTMLParagraphElement>(null);

	React.useLayoutEffect(() => {
		const element = textRef.current;
		if (element) {
			// Check if text is truncated by comparing scrollHeight to clientHeight
			// scrollHeight is the full content height, clientHeight is the visible (clamped) height
			setNeedsTruncation(element.scrollHeight > element.clientHeight + 1); // +1 for rounding tolerance
		}
	}, [text, maxLines]);

	const toggleExpanded = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<div className={css(styleSheet.container)}>
			<p
				ref={textRef}
				className={css(styleSheet.text, !isExpanded && styleSheet.textCollapsed)}
				style={!isExpanded ? { WebkitLineClamp: maxLines } : undefined}
			>
				{prefix && <span className={css(styleSheet.prefix)}>{prefix}</span>}
				{text}
			</p>
			{needsTruncation && (
				<button
					className={css(styleSheet.toggleButton)}
					onClick={toggleExpanded}
					type='button'
					aria-expanded={isExpanded}
				>
					{isExpanded ? 'Show less' : 'Show more'}
				</button>
			)}
		</div>
	);
};
